import React from 'react';
import { Link } from 'react-router-dom';
import { FiActivity, FiTarget, FiTrendingUp, FiBook, FiAward, FiShield } from 'react-icons/fi';
import { motion } from 'framer-motion';
import Button from '../components/ui/Button';

// --- Animation Variants ---
const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};

const floatAnimation = {
  animate: {
    y: [0, -10, 0],
    transition: {
      duration: 6,
      repeat: Infinity,
      ease: 'easeInOut'
    }
  }
};

export default function Landing() {
  return (
    <div className="min-h-screen bg-slate-50 font-sans selection:bg-blue-900 selection:text-white relative overflow-hidden z-0">
      {/* Background Glow */}
      <motion.div 
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-blue-200/40 rounded-full blur-[120px] pointer-events-none -z-10"
        animate={{ scale: [1, 1.1, 1], opacity: [0.4, 0.6, 0.4] }}
        transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
      ></motion.div>

      {/* Navbar */}
      <motion.nav 
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className="relative z-20 flex justify-between items-center py-6 px-6 md:px-12 max-w-7xl mx-auto"
      >
        <div className="text-2xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
          <FiActivity className="text-blue-900" />
          ClinchFirst
        </div>
        <div className="flex gap-4">
          <Link to="/auth?mode=login">
            <Button variant="ghost" className="font-medium text-slate-600 hover:text-slate-900">Sign In</Button>
          </Link>
          <Link to="/auth?mode=signup">
            <Button variant="primary" className="font-medium shadow-sm">Get Started</Button>
          </Link>
        </div>
      </motion.nav>

      {/* Hero Section */}
      <motion.section 
        variants={staggerContainer}
        initial="hidden"
        animate="visible"
        className="relative z-20 px-6 md:px-12 py-20 md:py-32 max-w-7xl mx-auto text-center"
      >
        <motion.div variants={fadeUp} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-100/80 text-blue-900 font-medium text-sm mb-8">
          <span className="flex h-2 w-2 rounded-full bg-blue-600 animate-pulse"></span>
          The Ultimate Combat Sports Tracker
        </motion.div>
        
        <motion.h1 variants={fadeUp} className="text-5xl md:text-7xl font-extrabold text-slate-900 tracking-tight max-w-4xl mx-auto leading-[1.1]">
          Master Your <span className="text-blue-900">Martial Arts</span> Journey
        </motion.h1>
        
        <motion.p variants={fadeUp} className="mt-8 text-lg md:text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed">
          Log your training sessions for BJJ, Boxing, and MMA. Track your techniques, set actionable goals, and analyze your progress to become the best version of yourself.
        </motion.p>
        
        <motion.div variants={fadeUp} className="mt-10 flex flex-col sm:flex-row justify-center gap-4">
          <Link to="/auth?mode=signup">
            <Button size="lg" variant="primary" className="w-full sm:w-auto shadow-md hover:scale-105 transition-transform">
              Start Tracking Free
            </Button>
          </Link>
          <Link to="/auth?mode=login">
            <Button size="lg" variant="outline" className="w-full sm:w-auto hover:bg-slate-100 transition-colors">
              Welcome Back
            </Button>
          </Link>
        </motion.div>
        
        {/* Dashboard Preview / Mockup */}
        <motion.div variants={fadeUp} className="mt-20 relative max-w-6xl mx-auto">
          <div className="absolute inset-0 bg-gradient-to-t from-slate-50 via-transparent to-transparent z-10 bottom-0 h-32 mt-auto pointer-events-none"></div>
          
          <motion.div 
            variants={floatAnimation}
            animate="animate"
            className="bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden"
          >
            {/* Browser Header */}
            <div className="bg-slate-100 border-b border-slate-200 px-4 py-3 flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-400"></div>
              <div className="w-3 h-3 rounded-full bg-amber-400"></div>
              <div className="w-3 h-3 rounded-full bg-green-400"></div>
            </div>
            {/* Dashboard Image */}
            <div className="relative bg-slate-50">
              <img 
                src="https://cloud.sharemyimage.com/2026/07/24/clinchfirstscreenshot.png" 
                alt="ClinchFirst Dashboard Preview" 
                className="w-full h-auto object-cover object-top hover:scale-[1.02] transition-transform duration-700"
                loading="lazy"
              />
            </div>
          </motion.div>
        </motion.div>
      </motion.section>

      {/* Features Grid */}
      <section className="relative z-20 bg-white py-24 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            variants={fadeUp}
            className="text-center mb-20"
          >
            <h2 className="text-4xl font-bold text-slate-900 tracking-tight">Everything you need to level up</h2>
            <p className="mt-4 text-xl text-slate-600">Designed specifically for combat sports athletes.</p>
          </motion.div>
          
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            variants={staggerContainer}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-12"
          >
            {[
              { icon: FiActivity, title: "Session Tracking", desc: "Log every roll, drill, and spar. Keep track of your duration, wins, losses, and specific disciplines like BJJ, Boxing, and MMA." },
              { icon: FiTarget, title: "Goal Setting", desc: "Set actionable overall goals and break them down into milestones. Maintain daily habits to build consistency." },
              { icon: FiTrendingUp, title: "Advanced Analytics", desc: "Visualize your progress with detailed charts, win/loss ratios, and weekly summaries that highlight your strengths." },
              { icon: FiBook, title: "Technique Library", desc: "Build your own playbook. Document techniques, variations, and common mistakes to accelerate your learning." },
              { icon: FiAward, title: "Gamification", desc: "Earn XP and unlock achievements as you log sessions and hit milestones. Stay motivated every time you step on the mat." },
              { icon: FiShield, title: "Fitness & Hydration", desc: "Track your water intake, sleep quality, and daily readiness to ensure you're recovering properly between intense sessions." }
            ].map((feature, idx) => (
              <motion.div key={idx} variants={fadeUp} className="flex flex-col items-start group">
                <div className="w-14 h-14 bg-blue-50 rounded-xl flex items-center justify-center mb-6 group-hover:bg-blue-100 group-hover:scale-110 transition-all duration-300">
                  <feature.icon className="w-7 h-7 text-blue-900" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{feature.title}</h3>
                <p className="text-slate-600 leading-relaxed">{feature.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <motion.section 
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-50px" }}
        variants={fadeUp}
        className="relative z-20 bg-blue-900 py-20"
      >
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Ready to transform your training?</h2>
          <p className="text-blue-100 text-lg mb-10 max-w-2xl mx-auto">
            Join other martial artists who are taking their game to the next level. Sign up today and start tracking for free.
          </p>
          <Link to="/auth?mode=signup">
            <Button size="lg" className="bg-white text-blue-900 hover:bg-slate-100 hover:scale-105 transition-transform border-none font-bold shadow-lg px-8 py-4">
              Create Your Account
            </Button>
          </Link>
        </div>
      </motion.section>

      {/* Footer */}
      <footer className="relative z-20 bg-slate-950 py-12 text-center text-slate-400">
        <div className="max-w-7xl mx-auto px-6 flex flex-col items-center">
          <div className="flex items-center gap-2 font-bold text-xl text-white mb-6">
            <FiActivity className="text-blue-500" />
            ClinchFirst
          </div>
          <p className="mb-2">&copy; {new Date().getFullYear()} ClinchFirst. All rights reserved.</p>
          <p className="text-sm text-slate-500">Built for combat sports athletes.</p>
        </div>
      </footer>
    </div>
  );
}